import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { DeviceDetectService } from '@/services/deviceDetectService';
import styles from './RangeSlider.module.scss';

// Props Types
interface RangeSliderProps {
	classes?: {
		slider?: string;
		wrapper?: string;
		bar?: string;
		thumb?: string;
	};
	value: number;
	min: number;
	max: number;
	register: any;
	valueInThumb?: boolean;
	direction?: boolean;
}

// Animation
const thumbTextMotion = {
	initial: 'initial',
	animate: 'animate',
	exit: 'exit',
	variants: {
		initial: (direction: boolean) => ({
			x: direction ? '-100%' : '100%',
			scale: 0.5,
			opacity: 0,
		}),
		animate: {
			x: 0,
			scale: 1,
			opacity: 1,
		},
		exit: (direction: boolean) => ({
			x: direction ? '100%' : '-100%',
			scale: 0.5,
			opacity: 0,
		}),
	},
	transition: {
		type: 'tween',
		ease: 'linear',
		duration: 0.1,
	},
};

export const RangeSlider: React.FC<RangeSliderProps> = ({
	classes,
	value,
	max,
	min,
	register,
	valueInThumb,
	direction,
}) => {
	// Destructuring
	const { slider, wrapper, bar, thumb } = classes ?? {};

	// States
	const [focus, setFocus] = React.useState(false);
	const [isMobile, setIsMobile] = React.useState(false);

	// Actions
	const onBlur = () => {
		setFocus(false);
	};
	const onFocus = () => {
		setFocus(true);
	};

	// Helpers
	const correctingXPosition = () => {
		if (isMobile) {
			return 0;
		}

		if (value === max) {
			return 5;
		}

		return 0;
	};

	React.useEffect(() => {
		void (async () => {
			const mobile = await DeviceDetectService.isMobile();
			setIsMobile(mobile);
		})();
	}, []);

	return (
		<motion.div
			className={`
				${styles['range-slider']}
				${slider ?? ''}
			`}
			whileTap='tap'
			tabIndex={-1}
		>
			<input
				className={styles['range-slider__input']}
				type='range'
				min={min}
				max={max}
				step={1}
				{...register}
				onFocus={onFocus}
				onBlur={onBlur}
			/>
			<div className={`${styles['range-slider__wrapper']} ${wrapper ?? ''}`}>
				<motion.div
					animate={{ width: `${((value - min) * 100) / max}%` }}
					transition={{
						type: 'tween',
						ease: 'linear',
						duration: 0.2,
					}}
					className={`${styles['range-slider__bar']} ${bar ?? ''}`}
				>
					<motion.div
						initial={{
							x: 0,
							left: isMobile ? 'calc(96% - 5px)' : 'calc(96% - 5px)',
							y: '-50%',
							boxShadow: '0 0 0 3px rgba(69, 203, 176, 0)',
						}}
						animate={{
							x: correctingXPosition(),
							left: isMobile ? 'calc(96% - 5px)' : 'calc(96% - 5px)',
							y: '-50%',
							boxShadow: focus
								? '0 0 0 3px rgba(69, 203, 176, 0.24)'
								: '0 0 0 3px rgba(69, 203, 176, 0)',
						}}
						variants={{
							tap: {
								scale: 1.25,
								boxShadow: '0 0 0 3px rgba(69, 203, 176, 0)',
							},
						}}
						transition={{ duration: 0.25 }}
						className={`
							${styles['range-slider__thumb']}
							${value === min ? styles['range-slider__thumb_empty'] : ''}
							${value === max ? styles['range-slider__thumb_complete'] : ''}
							${thumb ?? ''}
						`}
					>
						{valueInThumb ? (
							<AnimatePresence initial={false} mode='popLayout'>
								<motion.span
									className={styles['range-slider__thumb-text']}
									// Animation
									key={value}
									{...thumbTextMotion}
									custom={direction}
								>
									{value}
								</motion.span>
							</AnimatePresence>
						) : null}
					</motion.div>
				</motion.div>
			</div>
		</motion.div>
	);
};
